import React from 'react';
import {
  Dialog,
  DialogTitle,
  List,
  ListItemIcon,
  ListItemText,
  Radio,
  Button,
  ListItemButton,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { useMarkets, useMarketShippingMethods } from '../../api/product';
import type { Market, ShippingMethod } from '../../types/product';
import { useTranslation } from 'react-i18next';
import { formatNumberMax1Digit } from '../../utils/numberUtils';
import { getMarketName } from '../../utils/productUtils';

interface ShippingMethodDialogProps {
  open: boolean;
  onClose: () => void;
  selectedMarket: {
    name: string;
    marketId: number;
  } | null;
  onSelectMarket: (marketId: number, name: string) => void;
}

const ShippingMethodDialog: React.FC<ShippingMethodDialogProps> = ({
  open,
  onClose,
  selectedMarket,
  onSelectMarket,
}) => {
  const { t, i18n } = useTranslation();
  const { marketsData } = useMarkets();
  const { shippingMethodsData } = useMarketShippingMethods();

  const handleSelectMarket = (marketId: number, name: string) => {
    onSelectMarket(marketId, name);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        style={{
          fontWeight: 700,
          fontSize: 20,
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        Select your shipping region:
      </DialogTitle>
      <List>
        {(marketsData || []).map((market: Market) => {
          const isSelected =
            selectedMarket && selectedMarket.marketId === market.id;
          // Find the free shipping method for this market
          const shippingMethod = (shippingMethodsData || []).find(
            (method: ShippingMethod) =>
              method.marketId === market.id && method.type === 'free_shipping'
          );
          const minAmount = shippingMethod?.minAmount;
          return (
            <ListItemButton
              key={market.id}
              onClick={() => handleSelectMarket(market.id, market.name)}
              style={{
                background: isSelected ? '#b48b6a' : '#fff',
                color: isSelected ? '#fff' : '#131518',
                marginBottom: 12,
                alignItems: 'flex-start',
                padding: '20px 24px',
                display: 'flex',
              }}
            >
              <ListItemIcon
                style={{
                  minWidth: 36,
                  color: isSelected ? '#fff' : '#b48b6a',
                }}
              >
                <PublicIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <span style={{ fontWeight: 700, fontSize: 18 }}>
                    {getMarketName(market, i18n.language)}
                  </span>
                }
                secondary={
                  minAmount ? (
                    <span style={{ fontWeight: 500, fontSize: 14 }}>
                      {t('cart.freeShippingOver').replace('{amount}', `HK$${formatNumberMax1Digit(minAmount)}`)}
                    </span>
                  ) : null
                }
              />
              {isSelected && <Radio checked style={{ color: '#fff' }} />}
            </ListItemButton>
          );
        })}
      </List>
      <Button onClick={onClose} style={{ margin: 16 }}>
        Close
      </Button>
    </Dialog>
  );
};

export default ShippingMethodDialog; 